import { Component, effect, ElementRef, OnInit, signal, ViewChild, ViewEncapsulation } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Tournament } from '../../models/tournament';
import { TournamentService } from '../../services/tournament.service';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Menubar } from 'primeng/menubar';
import { MenuItem } from 'primeng/api';
import { CommonModule } from '@angular/common';
import _ from 'lodash';
import { LogService } from '../../services/log.service';
import { ButtonModule } from 'primeng/button';
import { TeamService } from '../../services/team.service';
import { PopoverModule } from 'primeng/popover';
import { TextareaModule } from 'primeng/textarea';
import { FormsModule } from '@angular/forms';
import { environment } from '../../../environments/environment';
import { GROUPS_IFRAME } from '../../../assets/iframes/groups-iframe';
import { STANDINGS_IFRAME } from '../../../assets/iframes/standings-iframe';
import { ROUNDS_IFRAME } from '../../../assets/iframes/rounds-iframe';
import { AuxiliarService } from '../../services/auxiliar.service';
import { TooltipModule } from 'primeng/tooltip';
import { SearchFieldComponent } from '../../components/search-field/search-field.component';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-tournament-detail',
  imports: [
    ButtonModule,
    CommonModule,
    Menubar,
    RouterModule,
    TranslateModule,
    PopoverModule,
    SearchFieldComponent,
    TextareaModule,
    TooltipModule,
    FormsModule
  ],
  templateUrl: './tournament-detail.component.html',
  styleUrl: './tournament-detail.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class TournamentDetailComponent implements OnInit {
  @ViewChild('menubarButton', { static: false }) menubarButton!: ElementRef;

  public _ = _;
  public items: MenuItem[] | undefined;
  public tournament = signal<Tournament | undefined>(undefined);

  get iframeGroups(): string {
    const url = environment.front_url + environment.public_path.groups + this.tournament()?.id;
    return _.replace(GROUPS_IFRAME, ':url', url);
  }

  get iframeStandings(): string {
    const url = environment.front_url + environment.public_path.standings + this.tournament()?.id;
    return _.replace(STANDINGS_IFRAME, ':url', url);
  }

  get iframeRounds(): string {
    const url = environment.front_url + environment.public_path.rounds + this.tournament()?.id;
    return _.replace(ROUNDS_IFRAME, ':url', url);
  }

  constructor(
    public authService: AuthService,
    public auxiliarService: AuxiliarService,
    private logService: LogService,
    private route: ActivatedRoute,
    private router: Router,
    public teamService: TeamService,
    public tournamentService: TournamentService,
    private translate: TranslateService,
  ){
    effect(() => {
      this.logService.debug('selected tournament', this.tournament()); // comment is necessary
      this.buildMenu();
    });
  }

  ngOnInit(): void {
    this.tournament = this.tournamentService.tournament;
    const tournamentId = this.route.snapshot.paramMap.get('id');
    if(tournamentId) this.tournamentService.getTournament(tournamentId);
    if(!this.tournament()?.id) this.router.navigate(['admin', 'tournaments']);

    this.translate.onLangChange
      .subscribe(() => {
        this.buildMenu();
      });
  }

  private buildMenu(): void {
    this.items = [
      {
        id: 'teams',
        label: this.translate.instant('menu.teams'),
        command: () => {
          this.router.navigate(['admin', 'tournaments', this.tournament()?.id, 'teams']);
          this.tournamentService.setTournamentSection('teams');
        },
        isActive: () => _.includes(this.router.url, '/teams'),
      },
      {
        id: 'groups',
        label: this.translate.instant('menu.groups'),
        command: () => {
          this.router.navigate(['admin', 'tournaments', this.tournament()?.id, 'groups']);
          this.tournamentService.setTournamentSection('groups');
        },
        isActive: () => _.includes(this.router.url, '/groups'),
        disabled: this.tournament()?.stage === 'DRAFT',
      },
      {
        id: 'rounds',
        label: this.translate.instant('menu.rounds'),
        command: () => {
          this.router.navigate(['admin', 'tournaments', this.tournament()?.id, 'rounds']);
          this.tournamentService.setTournamentSection('rounds');
        },
        isActive: () => _.includes(this.router.url, '/rounds'),
        disabled: _.includes(['DRAFT', 'GROUPS'], this.tournament()?.stage),
      },
      {
        id: 'playoffs',
        label: this.translate.instant('menu.playoffs'),
        command: () => {
          this.router.navigate(['admin', 'tournaments', this.tournament()?.id, 'playoffs']);
          this.tournamentService.setTournamentSection('playoffs');
        },
        isActive: () => _.includes(this.router.url, '/playoffs'),
        disabled: this.tournament()?.stage !== 'PLAYOFFS',
      },
      {
        id: 'standing',
        label: this.translate.instant('menu.standing'),
        command: () => {
          this.router.navigate(['admin', 'tournaments', this.tournament()?.id, 'standing']);
          this.tournamentService.setTournamentSection('standing');
        },
        isActive: () => _.includes(this.router.url, '/standing'),
        disabled: _.includes(['DRAFT', 'GROUPS'], this.tournament()?.stage),
      },
    ];

    const activeItem = this.router.url.split('/').pop();
    if(!_.includes(this.router.url, '/rounds/' + activeItem)) {
      // get menu to navigate
      let menuToNav = _.includes(['groups', 'standing', 'rounds', 'playoffs'], activeItem) ? activeItem : 'teams';
      // check tournament stage
      if(_.includes(['standing', 'rounds', 'playoffs'], menuToNav) && _.includes(['DRAFT', 'GROUPS'], this.tournament()?.stage)) {
        menuToNav = this.tournament()?.stage === 'GROUPS' ? 'groups' : 'teams';
      } else if(_.includes(['playoffs'], menuToNav) && this.tournament()?.stage !== 'PLAYOFFS') {
        menuToNav = 'standing';
      }
      // navigate
      this.router.navigate(['admin', 'tournaments', this.tournament()?.id, menuToNav]);
      // set signal section
      this.tournamentService.setTournamentSection(menuToNav || 'teams');
    }
  }

  public isCollapsed(): boolean {
    return this.menubarButton?.nativeElement.offsetParent !== null;
  }
}
